import type {
  AIActionKey,
  EntitlementReasonCode,
  FeatureKey,
  SubscriptionCatalog,
  SubscriptionSnapshot,
  UpgradePromptDefinition,
  UpgradePromptKey,
} from '../../../types/src/subscription';
import { comparePlanTiers, subscriptionCatalog } from './catalog';

function isAiActionKey(
  catalog: SubscriptionCatalog,
  key: AIActionKey | FeatureKey | string,
): key is AIActionKey {
  return key in catalog.aiActions;
}

export function selectUpgradePrompt(
  snapshot: SubscriptionSnapshot,
  key: AIActionKey | FeatureKey,
  catalog: SubscriptionCatalog = subscriptionCatalog,
  reasonCode?: EntitlementReasonCode,
): UpgradePromptDefinition | null {
  if (isAiActionKey(catalog, key)) {
    if (key.startsWith('onboarding.')) {
      return null;
    }

    if (snapshot.planSet === 'individual') {
      if (reasonCode === 'ai_daily_limit_reached') {
        return snapshot.tier === 'free'
          ? catalog.prompts['individual.limit.free']
          : snapshot.tier === 'plus'
            ? catalog.prompts['individual.limit.plus']
            : catalog.prompts['individual.limit.pro'];
      }

      return snapshot.tier === 'free'
        ? catalog.prompts['individual.plus']
        : snapshot.tier === 'plus'
          ? catalog.prompts['individual.pro']
          : null;
    }

    if (reasonCode === 'seat_required') {
      return catalog.prompts['employer.seat.required'];
    }

    if (reasonCode === 'workspace_ai_limit_reached') {
      return catalog.prompts['employer.workspace.limit.free'];
    }

    const workspaceTier = snapshot.workspace?.workspaceTier ?? 'free';
    if (comparePlanTiers(workspaceTier, 'pro') >= 0) {
      return catalog.prompts['employer.pro'];
    }

    return catalog.prompts['employer.plus'];
  }

  const feature = catalog.features[key];
  if (feature.planSet === 'individual') {
    return feature.minimumTier === 'pro'
      ? catalog.prompts['individual.pro']
      : catalog.prompts['individual.plus'];
  }

  return feature.minimumTier === 'pro'
    ? catalog.prompts['employer.pro']
    : catalog.prompts['employer.plus'];
}

export function getUpgradePromptByKey(
  key: UpgradePromptKey | null | undefined,
  catalog: SubscriptionCatalog = subscriptionCatalog,
) {
  if (!key) {
    return null;
  }

  return catalog.prompts[key] ?? null;
}
